import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FaSliders} from 'react-icons/fa6';
import './CategorySelector.css';

const CategorySelector = ({ categories, selectedCategories = [], onSelectCategories }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    // Calculate dropdown position relative to button
    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 10,
          left: rect.left,
        });
      }
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    // Add listener after a brief delay to ensure dropdown is rendered
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleCategoryToggle = (category) => {
    const newSelected = selectedCategories.includes(category)
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    
    if (onSelectCategories) {
      onSelectCategories(newSelected);
    }
  };

  const handleSelectAll = () => {
    if (onSelectCategories) {
      onSelectCategories(selectedCategories.length === categories.length ? [] : [...categories]);
    }
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const dropdownContent = isOpen ? (
    <div 
      className="filter-dropdown"
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: `${dropdownPosition.top}px`,
        left: `${dropdownPosition.left}px`,
      }}
    >
      <div className="filter-dropdown-header">
        <button
          className="select-all-button"
          onClick={handleSelectAll}
        >
          {selectedCategories.length === categories.length ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <div className="filter-dropdown-list">
        {categories.map((category) => (
          <label key={category} className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={selectedCategories.includes(category)}
              onChange={() => handleCategoryToggle(category)}
              className="filter-checkbox"
            />
            <span className="filter-checkbox-text">{category}</span>
          </label>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="category-selector">
      <button
        ref={buttonRef}
        className="filter-button"
        onClick={handleButtonClick}
        aria-label="Filter categories"
      >
        <FaSliders className="filter-icon" />
        <span className="filter-text">Filter</span>
      </button>
      
      {isOpen && createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default CategorySelector;

